/**
 * Temu 承诺发货时效（shipmentLimitSecond，单位秒）。
 * 规则来源：官方「承诺发货时效说明」——按站点 × 普通品/定制品划分可选工作日。
 */

import { regionOfSiteId } from '@/temu-platform/utils/temuDeploymentRegions';
import i18n from '@/i18n';

function salesText(key, fallback, options) {
  if (!i18n.isInitialized) return fallback;
  return i18n.t(key, { ns: 'workbench', defaultValue: fallback, ...(options || {}) }) || fallback;
}

const SECONDS_PER_DAY = 86400;

/** @typedef {'US'|'MX'|'CL'|'PE'|'CO'|'EC'|'EU'|'OTHER'} TemuShipmentSiteGroup */

/**
 * 文档枚举：站点分组 → 普通品 / 定制品可选工作日。
 * 写法与官方表一致：`1/2/7/9`、`1/2/7-13`、`1/2/3/7-16`。
 */
const SHIPMENT_DAY_SPECS = Object.freeze({
  US: { regular: '1/2/7/9', custom: '1/2/3/7/9/10/12' },
  MX: { regular: '1/2/7-13', custom: '1/2/3/7-16' },
  CL: { regular: '1/2/7-12', custom: '1/2/3/7-15' },
  PE: { regular: '1/2/7-15', custom: '1/2/3/7-18' },
  CO: { regular: '1/2/7-16', custom: '1/2/3/7-19' },
  EC: { regular: '1/2/10-20', custom: '1/2/3/10-23' },
  EU: { regular: '1/2/8-12', custom: '1/2/3/8-15' },
  OTHER: { regular: '1/2', custom: '1/2/3' },
});

/** 有独立时效表的站点（非「其他」/欧盟聚合） */
const SPECIAL_SITE_GROUP = Object.freeze({
  100: 'US',
  110: 'MX',
  125: 'CL',
  163: 'PE',
  164: 'CO',
  178: 'EC',
});

/**
 * @param {string} spec
 * @returns {number[]}
 */
export function expandTemuShipmentDaySpec(spec) {
  const out = [];
  const seen = new Set();
  String(spec || '')
    .split('/')
    .map((part) => part.trim())
    .filter(Boolean)
    .forEach((part) => {
      const range = part.match(/^(\d+)\s*-\s*(\d+)$/);
      if (range) {
        const from = Number(range[1]);
        const to = Number(range[2]);
        if (!(from > 0 && to >= from)) return;
        for (let d = from; d <= to; d += 1) {
          if (!seen.has(d)) {
            seen.add(d);
            out.push(d);
          }
        }
        return;
      }
      const day = Number(part);
      if (Number.isFinite(day) && day > 0 && !seen.has(day)) {
        seen.add(day);
        out.push(day);
      }
    });
  return out;
}

/**
 * @param {unknown} siteId
 * @returns {TemuShipmentSiteGroup}
 */
export function resolveTemuShipmentSiteGroup(siteId) {
  const id = Number(siteId);
  if (!Number.isFinite(id) || id <= 0) return 'OTHER';
  const special = SPECIAL_SITE_GROUP[id];
  if (special) return special;
  if (regionOfSiteId(id) === 'EU') return 'EU';
  return 'OTHER';
}

/**
 * @param {unknown} raw
 * @returns {boolean}
 */
export function isTemuCustomProductMode(raw) {
  if (raw == null) return false;
  if (typeof raw === 'boolean') return raw;
  if (typeof raw === 'object') {
    return isTemuCustomProductMode(
      raw.isCustomProduct
      ?? raw.personalizationSwitch
      ?? raw.customProduct
      ?? raw.shipmentProductMode,
    );
  }
  const text = String(raw).trim().toLowerCase();
  if (!text) return false;
  if (text === '1' || text === 'true' || text === 'custom' || text === '定制品') return true;
  if (text === '0' || text === 'false' || text === 'regular' || text === '普通品') return false;
  const n = Number(text);
  return Number.isFinite(n) && n === 1;
}

/**
 * @param {unknown} siteId
 * @param {boolean} customProduct
 * @returns {number[]} 工作日列表
 */
export function listTemuShipmentLimitDays(siteId, customProduct = false) {
  const group = resolveTemuShipmentSiteGroup(siteId);
  const spec = SHIPMENT_DAY_SPECS[group] || SHIPMENT_DAY_SPECS.OTHER;
  return expandTemuShipmentDaySpec(customProduct ? spec.custom : spec.regular);
}

/**
 * 多站点时取交集（最严）；无站点时按「其他」兜底。
 * @param {Array<string|number>|string|number|null|undefined} siteIds
 * @param {boolean} customProduct
 * @returns {number[]}
 */
export function listTemuShipmentLimitDaysForSites(siteIds, customProduct = false) {
  const ids = Array.isArray(siteIds)
    ? siteIds
    : (siteIds == null || siteIds === '' ? [] : [siteIds]);
  const normalized = ids.map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0);
  if (!normalized.length) {
    return listTemuShipmentLimitDays(null, customProduct);
  }
  let intersect = null;
  normalized.forEach((id) => {
    const days = listTemuShipmentLimitDays(id, customProduct);
    const set = new Set(days);
    intersect = intersect == null
      ? days.slice()
      : intersect.filter((d) => set.has(d));
  });
  return Array.isArray(intersect) && intersect.length
    ? intersect
    : listTemuShipmentLimitDays(normalized[0], customProduct);
}

/**
 * @param {number[]} days
 * @returns {{ value: string, label: string }[]}
 */
export function buildTemuShipmentLimitOptions(days) {
  return (Array.isArray(days) ? days : [])
    .filter((d) => Number.isFinite(d) && d > 0)
    .map((day) => ({
      value: String(day * SECONDS_PER_DAY),
      label: salesText('publishSales.workdays', `${day} 个工作日`, { count: day }),
    }));
}

/**
 * @param {object} [params]
 * @param {Array<string|number>|string|number|null|undefined} [params.siteIds]
 * @param {unknown} [params.customProduct]
 * @returns {{ value: string, label: string }[]}
 */
export function getTemuShipmentLimitOptions({ siteIds, customProduct } = {}) {
  const days = listTemuShipmentLimitDaysForSites(siteIds, isTemuCustomProductMode(customProduct));
  return buildTemuShipmentLimitOptions(days);
}

/**
 * 当前值不在可选列表时回落到 2 天（若有）或首项。
 * @param {unknown} currentSeconds
 * @param {{ value: string }[]} options
 * @returns {string}
 */
export function pickTemuShipmentLimitSecond(currentSeconds, options) {
  const list = Array.isArray(options) ? options : [];
  const cur = String(currentSeconds ?? '').trim();
  if (cur && list.some((o) => String(o.value) === cur)) return cur;
  const twoDay = list.find((o) => String(o.value) === String(2 * SECONDS_PER_DAY));
  if (twoDay) return String(twoDay.value);
  return list[0] ? String(list[0].value) : '';
}

/**
 * @param {unknown} seconds
 * @param {{ value: string }[]} options
 * @returns {boolean}
 */
export function isValidTemuShipmentLimitSecond(seconds, options) {
  const cur = String(seconds ?? '').trim();
  if (!cur) return false;
  return (Array.isArray(options) ? options : []).some((o) => String(o.value) === cur);
}
